import { FeatureCategoryType } from '@granlupo/api-client/generated/content/zeus'
import NextImage from 'next/legacy/image'
import type { FunctionComponent } from 'react'
import type { ProductEvaluationSummaryResult } from '../../data/ProductEvaluationSummaryFragment'
import type { ProductFeatureResult } from '../../data/ProductFeatureFragment'
import style from './SummaryBanner.module.sass'
import { SummaryList } from './SummaryList'

type SummaryBannerProps = {
	energy: string
	activity: JSX.Element
	features: ProductFeatureResult[] | undefined
	eliminationDiet: boolean
	hasDetailedComposition?: boolean
	summaryTitles: ProductEvaluationSummaryResult[]
}

export const SummaryBanner: FunctionComponent<SummaryBannerProps> = ({
	energy,
	activity,
	features,
	eliminationDiet = false,
	summaryTitles,
	hasDetailedComposition = false,
}) => {
	const positiveFeatures = features?.filter((feature) => feature.category === FeatureCategoryType.positive)
	const negativeFeatures = features?.filter(
		(feature) => feature.category === FeatureCategoryType.negative && feature.value !== 'minusOne',
	)
	const allergyFeatures = features?.filter((feature) => feature.category === FeatureCategoryType.allergy)

	return (
		<div className={style.wrapper}>
			{(energy || activity) && (
				<SummaryList
					summaryCategory={{
						title: summaryTitles[0].title,
						id: summaryTitles[0].remoteId,
						icon: (
							<div className={style.iconWrapper}>
								{summaryTitles[0].icon && (
									<NextImage
										src={summaryTitles[0].icon?.url}
										alt={summaryTitles[0].icon?.alt ?? ''}
										title={summaryTitles[0].icon?.alt}
										objectFit="cover"
										layout="fill"
									/>
								)}
							</div>
						),
					}}
					extraContent={
						<div>
							<>
								<div className={style.text}>{energy}</div>
								<div className={style.text}>{activity}</div>
							</>
							{eliminationDiet && <div className={style.text}>{eliminationDiet}</div>}
						</div>
					}
				/>
			)}
			{hasDetailedComposition && positiveFeatures && positiveFeatures.length > 0 && (
				<SummaryList
					summaryCategory={{
						title: summaryTitles[1].title,
						id: summaryTitles[1].remoteId,
						icon: (
							<div className={style.iconWrapper}>
								{summaryTitles[1].icon && (
									<NextImage
										src={summaryTitles[1].icon?.url}
										alt={summaryTitles[1].icon?.alt ?? ''}
										title={summaryTitles[1].icon?.alt}
										objectFit="cover"
										layout="fill"
									/>
								)}
							</div>
						),
						values: positiveFeatures,
					}}
				/>
			)}
			{hasDetailedComposition && negativeFeatures && negativeFeatures.length > 0 && (
				<SummaryList
					summaryCategory={{
						title: summaryTitles[2].title,
						id: summaryTitles[2].remoteId,
						icon: (
							<div className={style.iconWrapper}>
								{summaryTitles[2].icon && (
									<NextImage
										src={summaryTitles[2].icon?.url}
										alt={summaryTitles[2].icon?.alt ?? ''}
										title={summaryTitles[2].icon?.alt}
										objectFit="cover"
										layout="fill"
									/>
								)}
							</div>
						),
						values: negativeFeatures,
					}}
				/>
			)}
			{hasDetailedComposition && allergyFeatures && allergyFeatures.length > 0 && (
				<SummaryList
					summaryCategory={{
						title: summaryTitles[3].title,
						id: summaryTitles[3].remoteId,
						icon: (
							<div className={style.iconWrapper}>
								{summaryTitles[3].icon && (
									<NextImage
										src={summaryTitles[3].icon?.url}
										alt={summaryTitles[3].icon?.alt ?? ''}
										title={summaryTitles[3].icon?.alt}
										objectFit="cover"
										layout="fill"
									/>
								)}
							</div>
						),
						values: allergyFeatures,
					}}
				/>
			)}
		</div>
	)
}
